import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { FileText, Shield, Car, Search, UserPlus, Download, Mail, MessageCircle, Camera, Loader2, Check, Save, CheckCircle2, Truck, Gift, X } from 'lucide-react';

const TVA_RATE = 8.1;
const WARRANTY_OPTIONS = [
  { id: null, label: 'Sans garantie', months: 0, km: 0 },
  { id: '12_mois', label: '12 mois / 20\'000 km', months: 12, km: 20000 },
  { id: '24_mois', label: '24 mois / 40\'000 km', months: 24, km: 40000 },
];

// All OCR fields from carte grise
const EMPTY_TRADEIN = {
  brand: '', model: '', plate_number: '', chassis_number: '', color: '', color_code: '',
  body_type: '', first_registration: '', expertise_date: '', expertise_canton: '',
  displacement_cc: 0, power_kw: 0, empty_weight_kg: 0, total_weight_kg: 0,
  seats_total: 0, seats_front: 0, vehicle_genre: '', serial_number: '',
  type_approval: '', emission_code: '', vehicle_code: '', special_usage: '',
  payload_kg: 0, towing_weight_kg: 0, co2_emissions: 0,
  owner_name: '', owner_street: '', owner_postal_code: '', owner_city: '',
  owner_dob: '', owner_origin: '',
  insurance_name: '', insurance_number: '',
  mileage: 0, value: 0, canton_notes: '',
};

const SaleProcessDialog = ({ vehicle, open, onClose, onContractSaved, existingContract }) => {
  const [tab, setTab] = useState('contrat');
  const [contractId, setContractId] = useState(null);
  const [contractStatus, setContractStatus] = useState('new');

  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ first_name: '', last_name: '', phone: '', email: '', address: '', city: '' });

  const [priceTTC, setPriceTTC] = useState(0);
  const [priceHT, setPriceHT] = useState(0);

  const [warranty, setWarranty] = useState(null);
  const [warrantyPrice, setWarrantyPrice] = useState(0);

  const [hasTradeIn, setHasTradeIn] = useState(false);
  const [tradeIn, setTradeIn] = useState({...EMPTY_TRADEIN});
  const [scanQR, setScanQR] = useState(null);
  const [scanning, setScanning] = useState(false);

  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [offeredItems, setOfferedItems] = useState([]);
  const [newOfferedItem, setNewOfferedItem] = useState('');

  const [downPaymentAmount, setDownPaymentAmount] = useState(0);
  const [downPaymentMethod, setDownPaymentMethod] = useState('');

  const [saving, setSaving] = useState(false);
  const [concluding, setConcluding] = useState(false);

  useEffect(() => {
    if (vehicle && open) {
      if (existingContract) {
        // Pre-fill from existing contract
        setContractId(existingContract.id);
        setContractStatus(existingContract.status || 'draft');
        setPriceTTC(existingContract.price_ttc || 0);
        setPriceHT(existingContract.price_ht || Math.round((existingContract.price_ttc || 0) / (1 + TVA_RATE / 100) * 100) / 100);
        setTab('contrat');

        // Warranty
        if (existingContract.warranty && existingContract.warranty.type) {
          const wo = WARRANTY_OPTIONS.find(w => w.id === existingContract.warranty.type);
          setWarranty(wo || { id: existingContract.warranty.type, label: existingContract.warranty.label || '', months: existingContract.warranty.months || 0, km: existingContract.warranty.km || 0 });
          setWarrantyPrice(existingContract.warranty_price || existingContract.warranty.price || 0);
        } else {
          setWarranty(null); setWarrantyPrice(0);
        }

        // Trade-in
        if (existingContract.trade_in && existingContract.trade_in.brand) {
          setHasTradeIn(true);
          setTradeIn({ ...EMPTY_TRADEIN, ...existingContract.trade_in });
        } else {
          setHasTradeIn(false); setTradeIn({ ...EMPTY_TRADEIN });
        }

        // Other fields
        setDeliveryPrice(existingContract.delivery_price || 0);
        setOfferedItems(existingContract.offered_items || []);
        setDownPaymentAmount(existingContract.down_payment_amount || 0);
        setDownPaymentMethod(existingContract.down_payment_method || '');

        // Load client from contract
        if (existingContract.client_id && existingContract.client_info) {
          setSelectedClient({
            id: existingContract.client_id,
            first_name: existingContract.client_info.first_name || '',
            last_name: existingContract.client_info.last_name || '',
            phone: existingContract.client_info.phone || '',
            email: existingContract.client_info.email || '',
            address: existingContract.client_info.address || '',
            city: existingContract.client_info.city || '',
          });
        } else {
          setSelectedClient(null);
        }
      } else {
        // New contract - reset everything
        const ttc = vehicle.selling_price || 0;
        setPriceTTC(ttc);
        setPriceHT(Math.round(ttc / (1 + TVA_RATE / 100) * 100) / 100);
        setTab('contrat');
        setContractId(null);
        setContractStatus('new');
        setSelectedClient(null);
        setWarranty(null); setWarrantyPrice(0);
        setHasTradeIn(false);
        setTradeIn({...EMPTY_TRADEIN});
        setDeliveryPrice(0);
        setOfferedItems([]);
        setDownPaymentAmount(0);
        setDownPaymentMethod('');
      }
    }
  }, [vehicle, open, existingContract]);

  const searchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients${clientSearch ? `?search=${encodeURIComponent(clientSearch)}` : ''}`);
      setClients(res.data);
    } catch {}
  }, [clientSearch]);

  useEffect(() => { if (open) searchClients(); }, [searchClients, open]);

  const createNewClient = async () => {
    if (!newClient.first_name || !newClient.last_name) { toast.error('Nom et prenom requis'); return; }
    try {
      const res = await axios.post(`${API}/clients`, newClient);
      setSelectedClient(res.data);
      setShowNewClient(false);
      setNewClient({ first_name: '', last_name: '', phone: '', email: '', address: '', city: '' });
      toast.success('Client cree');
      searchClients();
    } catch { toast.error('Erreur creation client'); }
  };

  const tradeInValue = hasTradeIn ? (parseFloat(tradeIn.value) || 0) : 0;
  const total = priceTTC + warrantyPrice + deliveryPrice - tradeInValue;

  const saveContract = async () => {
    if (!selectedClient) { toast.error('Selectionnez un client'); setTab('contrat'); return; }
    setSaving(true);
    try {
      const payload = {
        vehicle_id: vehicle.id,
        client_id: selectedClient.id,
        price_ttc: priceTTC,
        warranty: warranty ? { type: warranty.id, months: warranty.months, km: warranty.km, price: warrantyPrice, label: warranty.label } : null,
        trade_in: hasTradeIn && tradeIn.brand ? tradeIn : null,
        delivery_price: deliveryPrice,
        offered_items: offeredItems,
        down_payment_amount: downPaymentAmount,
        down_payment_method: downPaymentMethod,
      };
      let res;
      if (contractId) {
        res = await axios.put(`${API}/sale-contracts/${contractId}`, payload);
      } else {
        res = await axios.post(`${API}/sale-contracts`, payload);
      }
      setContractId(res.data.id);
      setContractStatus('draft');
      toast.success('Contrat enregistre !');
      if (onContractSaved) onContractSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const concludeContract = async () => {
    if (!contractId) return;
    setConcluding(true);
    try {
      await axios.post(`${API}/sale-contracts/${contractId}/conclude`);
      setContractStatus('concluded');
      toast.success('Contrat conclu ! Vehicule vendu.');
      if (onContractSaved) onContractSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setConcluding(false); }
  };

  const buildPdfUrl = () => {
    const p = new URLSearchParams();
    if (selectedClient) p.set('client_id', selectedClient.id);
    if (warranty) { p.set('warranty_months', warranty.months); p.set('warranty_details', `${warranty.label} - CHF ${warrantyPrice}`); }
    p.set('price_ht', priceHT); p.set('price_ttc', priceTTC);
    if (deliveryPrice > 0) p.set('delivery_price', deliveryPrice);
    if (offeredItems.length > 0) p.set('offered_items', offeredItems.join(', '));
    if (downPaymentAmount > 0) {
      p.set('down_payment_amount', downPaymentAmount);
      if (downPaymentMethod) p.set('down_payment_method', downPaymentMethod);
    }
    if (hasTradeIn && tradeIn.brand) {
      p.set('tradein_brand', tradeIn.brand || '');
      p.set('tradein_model', tradeIn.model || '');
      p.set('tradein_plate', tradeIn.plate_number || '');
      p.set('tradein_chassis', tradeIn.chassis_number || '');
      p.set('tradein_color', tradeIn.color || '');
      p.set('tradein_first_reg', tradeIn.first_registration || '');
      if (tradeIn.mileage) p.set('tradein_km', tradeIn.mileage);
      if (tradeIn.value) p.set('tradein_value', tradeIn.value);
      if (tradeIn.expertise_date) p.set('tradein_expertise', tradeIn.expertise_date);
    }
    return `${API}/used-vehicles/${vehicle.id}/contrat-vente?${p.toString()}`;
  };

  const downloadContractPdf = async () => {
    try {
      const { downloadPdf } = await import('@/utils/api');
      await downloadPdf(buildPdfUrl(), `contrat_vente_${vehicle?.plate_number || 'doc'}.pdf`);
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Erreur lors du telechargement du PDF');
    }
  };

  const sendWhatsApp = () => {
    const phone = (selectedClient?.phone || '').replace(/\s/g, '').replace(/^0/, '+41');
    const msg = encodeURIComponent(`Bonjour,\nVoici le contrat de vente pour ${vehicle.brand} ${vehicle.model} (${vehicle.plate_number}).`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
    downloadContractPdf();
  };

  const startScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/used-vehicles/scan-sessions`);
      setScanQR(res.data.qr_code);
      const sid = res.data.id;
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts > 120) { clearInterval(poll); setScanning(false); setScanQR(null); return; }
        try {
          const sr = await axios.get(`${API}/used-vehicles/scan-sessions/${sid}`);
          if (sr.data.status === 'completed' && sr.data.result) {
            clearInterval(poll);
            const r = sr.data.result;
            setTradeIn(prev => {
              const updated = {...prev};
              Object.keys(EMPTY_TRADEIN).forEach(k => {
                if (r[k] !== undefined && r[k] !== '' && r[k] !== 0) updated[k] = r[k];
              });
              return updated;
            });
            setHasTradeIn(true);
            setScanning(false); setScanQR(null);
            toast.success('Carte grise scannee !');
          }
        } catch {}
      }, 1000);
    } catch { toast.error('Erreur scan'); setScanning(false); }
  };

  const addOfferedItem = () => {
    if (!newOfferedItem.trim()) return;
    setOfferedItems([...offeredItems, newOfferedItem.trim()]);
    setNewOfferedItem('');
  };

  if (!vehicle) return null;

  const tabs = [
    { id: 'contrat', label: 'Contrat', icon: FileText, done: !!selectedClient },
    { id: 'garantie', label: 'Garantie', icon: Shield, done: !!warranty },
    { id: 'reprise', label: 'Reprise', icon: Car, done: hasTradeIn && !!tradeIn.brand },
    { id: 'livraison', label: 'Livraison', icon: Truck, done: deliveryPrice > 0 },
    { id: 'offert', label: 'Offert', icon: Gift, done: offeredItems.length > 0 },
  ];
  const isConcluded = contractStatus === 'concluded';

  const TI = ({label, field, type = 'text'}) => (
    <div>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <Input type={type} value={tradeIn[field] || ''} onChange={e => setTradeIn({...tradeIn, [field]: type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value})} className="h-7 text-xs" data-testid={`ti-${field}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="sale-process-dialog">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="w-5 h-5 text-blue-600" />
            Vente — {vehicle.brand} {vehicle.model}
            <Badge className="bg-blue-100 text-blue-700 text-xs">{vehicle.plate_number}</Badge>
            {contractStatus === 'draft' && <Badge className="bg-amber-100 text-amber-700 text-xs">Brouillon</Badge>}
            {isConcluded && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Conclu</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex border-b px-2 overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${t.id}`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
                {t.done && <Check className="w-3 h-3 text-emerald-500" />}
              </button>
            );
          })}
        </div>

        <div className="p-4 space-y-4">

          {/* CONTRAT */}
          {tab === 'contrat' && (<>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500 mb-2 block">Client / Acheteur</Label>
              {selectedClient ? (
                <Card className="p-3 border-emerald-200 bg-emerald-50" data-testid="selected-client">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm">{selectedClient.first_name} {selectedClient.last_name}</p>
                      <p className="text-xs text-slate-500">{selectedClient.phone}</p>
                    </div>
                    {!isConcluded && <Button size="sm" variant="outline" className="text-xs" onClick={() => setSelectedClient(null)} data-testid="change-client-btn">Changer</Button>}
                  </div>
                </Card>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder="Rechercher..." className="pl-8 h-9" data-testid="client-search-input" />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setShowNewClient(!showNewClient)} data-testid="new-client-btn">
                      <UserPlus className="w-4 h-4 mr-1" /> Nouveau
                    </Button>
                  </div>
                  {showNewClient && (
                    <Card className="p-3 border-blue-200 bg-blue-50 space-y-2" data-testid="new-client-form">
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Prenom *</Label><Input value={newClient.first_name} onChange={e => setNewClient({...newClient, first_name: e.target.value})} className="h-8 text-sm" data-testid="nc-first-name" /></div>
                        <div><Label className="text-xs">Nom *</Label><Input value={newClient.last_name} onChange={e => setNewClient({...newClient, last_name: e.target.value})} className="h-8 text-sm" data-testid="nc-last-name" /></div>
                        <div><Label className="text-xs">Telephone</Label><Input value={newClient.phone} onChange={e => setNewClient({...newClient, phone: e.target.value})} className="h-8 text-sm" data-testid="nc-phone" /></div>
                        <div><Label className="text-xs">Email</Label><Input type="email" value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})} className="h-8 text-sm" data-testid="nc-email" /></div>
                      </div>
                      <Button size="sm" onClick={createNewClient} className="w-full bg-blue-600 text-white" data-testid="create-client-btn">Creer</Button>
                    </Card>
                  )}
                  {!showNewClient && (
                    <div className="max-h-32 overflow-y-auto space-y-0.5 border rounded-lg p-1" data-testid="client-list">
                      {clients.slice(0, 10).map(c => (
                        <button key={c.id} onClick={() => setSelectedClient(c)} className="w-full text-left px-3 py-2 rounded hover:bg-slate-50 flex justify-between text-sm" data-testid={`select-client-${c.id}`}>
                          <span className="font-medium">{c.first_name} {c.last_name}</span>
                          <span className="text-xs text-slate-400">{c.phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500 mb-2 block">Prix de vente</Label>
              <Card className="p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Prix HT (CHF)</Label>
                    <Input type="number" value={priceHT || ''} readOnly className="h-9 font-mono bg-slate-50" data-testid="price-ht" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Prix TTC (CHF) — TVA {TVA_RATE}%</Label>
                    <Input type="number" value={priceTTC || ''} readOnly className="h-9 font-mono font-bold text-blue-700 bg-slate-50" data-testid="price-ttc" />
                  </div>
                </div>
              </Card>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500 mb-2 block">Acompte verse</Label>
              <Card className="p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Montant (CHF)</Label>
                    <Input type="number" value={downPaymentAmount || ''} onChange={e => setDownPaymentAmount(parseFloat(e.target.value) || 0)} placeholder="0" className="h-9 font-mono" data-testid="down-payment-amount" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Mode de paiement</Label>
                    <div className="flex gap-1.5 mt-1">
                      {[{id: 'especes', label: 'Especes'}, {id: 'carte', label: 'Carte'}, {id: 'virement', label: 'Virement'}].map(m => (
                        <button key={m.id} type="button" onClick={() => setDownPaymentMethod(downPaymentMethod === m.id ? '' : m.id)}
                          className={`flex-1 py-1.5 px-2 rounded text-xs font-medium border transition-colors ${downPaymentMethod === m.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                          data-testid={`down-payment-${m.id}`}>
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </>)}

          {/* GARANTIE */}
          {tab === 'garantie' && (
            <div className="space-y-3">
              {WARRANTY_OPTIONS.map(w => (
                <Card key={w.id || 'none'} className={`p-3 cursor-pointer transition-all ${warranty?.id === w.id ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-400' : 'hover:bg-slate-50'}`}
                  onClick={() => { setWarranty(w.id ? w : null); if (!w.id) setWarrantyPrice(0); }}
                  data-testid={`warranty-${w.id || 'none'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${warranty?.id === w.id ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                      {warranty?.id === w.id && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-sm font-medium">{w.label}</span>
                  </div>
                </Card>
              ))}
              {warranty && warranty.id && (
                <div>
                  <Label className="text-xs text-slate-500">Prix de la garantie (CHF)</Label>
                  <Input type="number" value={warrantyPrice || ''} onChange={e => setWarrantyPrice(parseFloat(e.target.value) || 0)} placeholder="A definir" className="h-9 font-mono mt-1" data-testid="warranty-price" />
                </div>
              )}
            </div>
          )}

          {/* REPRISE */}
          {tab === 'reprise' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase text-slate-500">Vehicule de reprise</Label>
                <Button size="sm" variant={hasTradeIn ? 'default' : 'outline'}
                  onClick={() => setHasTradeIn(!hasTradeIn)}
                  className={hasTradeIn ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  data-testid="toggle-tradein">
                  {hasTradeIn ? 'Reprise activee' : 'Activer la reprise'}
                </Button>
              </div>
              {hasTradeIn && (<>
                <Card className="p-3 border-dashed border-2 border-slate-300 text-center" data-testid="scan-section">
                  {scanQR ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Scannez avec le telephone</p>
                      <img src={scanQR} alt="QR" className="w-36 h-36 mx-auto" data-testid="scan-qr" />
                      <div className="flex items-center justify-center gap-2 text-xs text-amber-600"><Loader2 className="w-3 h-3 animate-spin" /> Attente...</div>
                      <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setScanQR(null); setScanning(false); }}>Annuler</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={startScan} disabled={scanning} data-testid="start-scan-btn">
                      <Camera className="w-4 h-4 mr-1" /> Scanner la carte grise
                    </Button>
                  )}
                </Card>

                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Proprietaire</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <TI label="Nom" field="owner_name" />
                    <TI label="Rue" field="owner_street" />
                    <TI label="NPA" field="owner_postal_code" />
                    <TI label="Ville" field="owner_city" />
                    <TI label="Date de naissance" field="owner_dob" />
                    <TI label="Origine" field="owner_origin" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Vehicule</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <TI label="Marque" field="brand" />
                    <TI label="Modele" field="model" />
                    <TI label="Plaque" field="plate_number" />
                    <TI label="Chassis / VIN" field="chassis_number" />
                    <TI label="Carrosserie" field="body_type" />
                    <TI label="Couleur" field="color" />
                    <TI label="Cylindree (cm3)" field="displacement_cc" type="number" />
                    <TI label="Puissance (kW)" field="power_kw" type="number" />
                    <TI label="Poids vide (kg)" field="empty_weight_kg" type="number" />
                    <TI label="Places" field="seats_total" type="number" />
                    <TI label="1ere mise en circ." field="first_registration" />
                    <TI label="Expertise" field="expertise_date" />
                    <TI label="Genre" field="vehicle_genre" />
                    <TI label="No. serie" field="serial_number" />
                    <TI label="Type approb." field="type_approval" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase text-slate-500">Assurance</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <TI label="Assurance RC" field="insurance_name" />
                    <TI label="No. police" field="insurance_number" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                  <div>
                    <Label className="text-xs text-slate-500">Kilometres</Label>
                    <Input type="number" value={tradeIn.mileage || ''} onChange={e => setTradeIn({...tradeIn, mileage: parseInt(e.target.value) || 0})} className="h-8 text-sm" data-testid="ti-mileage" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-amber-700">Valeur de reprise (CHF)</Label>
                    <Input type="number" value={tradeIn.value || ''} onChange={e => setTradeIn({...tradeIn, value: parseFloat(e.target.value) || 0})} className="h-8 text-sm font-bold border-amber-300" data-testid="ti-value" />
                  </div>
                </div>
              </>)}
            </div>
          )}

          {/* LIVRAISON */}
          {tab === 'livraison' && (
            <div className="space-y-4">
              <Label className="text-xs font-semibold uppercase text-slate-500 block">Forfait livraison</Label>
              <div className="grid grid-cols-3 gap-2">
                {[0, 200, 400, 600, 800, 1000].map(p => (
                  <Button key={p} size="sm" variant={deliveryPrice === p ? 'default' : 'outline'}
                    onClick={() => setDeliveryPrice(p)}
                    className={deliveryPrice === p ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    data-testid={`delivery-${p}`}>
                    {p === 0 ? 'Sans livraison' : `CHF ${p}.-`}
                  </Button>
                ))}
              </div>
              <div>
                <Label className="text-xs text-slate-500">Ou saisir un montant personnalise (CHF)</Label>
                <Input type="number" value={deliveryPrice || ''} onChange={e => setDeliveryPrice(parseFloat(e.target.value) || 0)} placeholder="Montant" className="h-9 font-mono mt-1" data-testid="delivery-custom" />
              </div>
              {deliveryPrice > 0 && (
                <Card className="p-3 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Forfait livraison: CHF {deliveryPrice.toLocaleString('fr-CH')}.-</span>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* OFFERT */}
          {tab === 'offert' && (
            <div className="space-y-4">
              <Label className="text-xs font-semibold uppercase text-slate-500 block">Elements offerts</Label>
              <div className="flex gap-2">
                <Input value={newOfferedItem} onChange={e => setNewOfferedItem(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addOfferedItem(); }}
                  placeholder="Ex: Tapis de sol, antivol, premiere revision..." className="h-9" data-testid="offered-input" />
                <Button size="sm" onClick={addOfferedItem} data-testid="add-offered-btn">Ajouter</Button>
              </div>
              {offeredItems.length > 0 ? (
                <div className="space-y-1">
                  {offeredItems.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Gift className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-sm">{item}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setOfferedItems(offeredItems.filter((_, j) => j !== i))} data-testid={`remove-offered-${i}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400">Ces elements seront mentionnes comme offerts dans le contrat</p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Gift className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Aucun element offert</p>
                </div>
              )}
            </div>
          )}

          {/* TOTAL + ACTIONS */}
          <div className="border-t pt-4 space-y-3">
            <Card className="p-3 bg-slate-50">
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="text-slate-500 py-0.5">Prix vehicule TTC</td>
                    <td className="text-right font-mono">CHF {priceTTC.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  {warranty && warranty.id && warrantyPrice > 0 && (
                    <tr>
                      <td className="text-slate-500 py-0.5">+ Garantie ({warranty.label})</td>
                      <td className="text-right font-mono">CHF {warrantyPrice.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  {deliveryPrice > 0 && (
                    <tr>
                      <td className="text-slate-500 py-0.5">+ Forfait livraison</td>
                      <td className="text-right font-mono">CHF {deliveryPrice.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  {hasTradeIn && tradeInValue > 0 && (
                    <tr>
                      <td className="text-slate-500 py-0.5">- Reprise ({tradeIn.brand} {tradeIn.model})</td>
                      <td className="text-right font-mono text-amber-700">- CHF {tradeInValue.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  {offeredItems.length > 0 && (
                    <tr><td className="text-green-600 py-0.5 text-xs" colSpan={2}>Offert: {offeredItems.join(', ')}</td></tr>
                  )}
                  <tr className="border-t">
                    <td className="text-slate-900 font-bold pt-2">TOTAL A PAYER</td>
                    <td className="text-right font-mono font-bold text-lg text-blue-700 pt-2" data-testid="total-amount">CHF {total.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  {downPaymentAmount > 0 && (
                    <>
                      <tr>
                        <td className="text-slate-500 py-0.5">Acompte verse {downPaymentMethod ? `(${downPaymentMethod === 'especes' ? 'Especes' : downPaymentMethod === 'carte' ? 'Carte' : 'Virement'})` : ''}</td>
                        <td className="text-right font-mono text-emerald-700">- CHF {downPaymentAmount.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}</td>
                      </tr>
                      <tr className="border-t">
                        <td className="text-slate-900 font-bold pt-1">SOLDE RESTANT</td>
                        <td className="text-right font-mono font-bold text-lg text-red-700 pt-1" data-testid="remaining-amount">CHF {(total - downPaymentAmount).toLocaleString('fr-CH', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </Card>

            {!isConcluded ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button onClick={saveContract} disabled={saving || !selectedClient} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-contract-btn">
                    <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Enregistrement...' : (contractId ? 'Mettre a jour' : 'Enregistrer Contrat')}
                  </Button>
                  {contractId && (
                    <Button onClick={concludeContract} disabled={concluding} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="conclude-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> {concluding ? 'En cours...' : 'Conclure'}
                    </Button>
                  )}
                </div>
                {contractId && (
                  <div className="flex gap-2">
                    <Button onClick={downloadContractPdf} variant="outline" className="flex-1" data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
                    <Button onClick={() => { downloadContractPdf(); toast.info(`Email: ${selectedClient?.email || ''}`); }} variant="outline" className="flex-1" data-testid="send-email-btn"><Mail className="w-4 h-4 mr-1" /> Email</Button>
                    <Button onClick={sendWhatsApp} variant="outline" className="flex-1" disabled={!selectedClient?.phone} data-testid="send-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Card className="p-3 bg-emerald-50 border-emerald-200 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-1" />
                  <p className="font-semibold text-emerald-700">Contrat conclu</p>
                </Card>
                <div className="flex gap-2">
                  <Button onClick={downloadContractPdf} variant="outline" className="flex-1" data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
                  <Button onClick={() => { downloadContractPdf(); }} variant="outline" className="flex-1" data-testid="send-email-btn"><Mail className="w-4 h-4 mr-1" /> Email</Button>
                  <Button onClick={sendWhatsApp} variant="outline" className="flex-1" disabled={!selectedClient?.phone} data-testid="send-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaleProcessDialog;
